import React, { useState } from 'react';
import { Button } from '@vaiu/ui/button';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Separator } from '@vaiu/ui/separator';
import { 
  Bitcoin,
  AlertCircle,
  Plus
} from 'lucide-react';
import { PaymentMethod, platformApi } from '@/services/platformApi';
import BitcoinWalletForm from './BitcoinWalletForm';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vaiu/ui/select';

interface PaymentMethodBitcoinProps {
  amount: number;
  currency: string;
  onSuccess: (newBalance: number, fundingDetails?: any) => void;
  onError?: (error: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
  className?: string;
  showSeparator?: boolean;
  paymentMethods?: PaymentMethod[];
}

/**
 * PaymentMethodBitcoin - Bitcoin payment method component
 * Allows users to pay with Bitcoin by selecting a saved wallet or adding a new one
 */
export const PaymentMethodBitcoin: React.FC<PaymentMethodBitcoinProps> = ({
  amount,
  currency,
  onSuccess,
  onError,
  onCancel,
  disabled = false,
  className,
  showSeparator = true,
  paymentMethods = []
}) => {
  const [selectedMethodId, setSelectedMethodId] = useState<string>('');
  const [showAddWallet, setShowAddWallet] = useState(false);
  const [loading, setLoading] = useState(false);

  const bitcoinMethods = paymentMethods.filter(m => m.type === 'bitcoin');

  const handleBitcoinPayment = async () => {
    if (!selectedMethodId) {
      if (onError) {
        onError('Please select a Bitcoin wallet');
      }
      return;
    }

    setLoading(true);
    try {
      // Bitcoin transactions require manual handling
      // Generate deposit address and wait for payment
      // For now, simulate the flow - this should be replaced with actual API call
      const result = {
        success: true,
        transactionId: `btc_${Date.now()}`,
        amount: amount,
        currency: currency
      };

      // In a real implementation, this would:
      // 1. Generate a Bitcoin address for the user to send to
      // 2. Monitor the blockchain for the transaction
      // 3. Credit the user's account once confirmed

      if (onSuccess) {
        onSuccess(amount, {
          source: 'bitcoin',
          amount: amount,
          currency: currency,
          paymentMethodType: 'bitcoin'
        });
      }
    } catch (error: any) {
      if (onError) {
        onError(error.message || 'Bitcoin payment failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddWallet = async (method: Omit<PaymentMethod, 'id' | 'createdAt'>) => {
    try {
      // Add wallet via API
      const newMethod = await platformApi.payments.addPaymentMethod(method);
      setShowAddWallet(false);
      setSelectedMethodId(newMethod.id!);
      // Refresh payment methods would be handled by parent
    } catch (error: any) {
      if (onError) {
        onError(error.message || 'Failed to add Bitcoin wallet');
      }
    }
  };

  if (showAddWallet) {
    return (
      <div className={className}>
        {showSeparator && (
          <div className="flex items-center gap-4 my-4">
            <Separator className="flex-1" />
            <span className="text-sm text-muted-foreground">OR</span>
            <Separator className="flex-1" />
          </div>
        )}
        
        <BitcoinWalletForm
          onSubmit={handleAddWallet}
          loading={loading}
          onCancel={() => setShowAddWallet(false)}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      {showSeparator && (
        <div className="flex items-center gap-4 my-4">
          <Separator className="flex-1" />
          <span className="text-sm text-muted-foreground">OR</span>
          <Separator className="flex-1" />
        </div>
      )}

      <div className="space-y-4">
        <div className="text-center py-2">
          <Bitcoin className="w-8 h-8 mx-auto text-orange-500 mb-2" />
          <h3 className="font-medium">Pay with Bitcoin</h3>
        </div>

        {bitcoinMethods.length > 0 ? (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Bitcoin Wallet</label>
              <Select value={selectedMethodId} onValueChange={setSelectedMethodId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a Bitcoin wallet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add-new">
                    <div className="flex items-center space-x-2">
                      <Plus className="w-4 h-4" />
                      <span>Add new Bitcoin wallet</span>
                    </div>
                  </SelectItem>
                  {bitcoinMethods.map((method) => (
                    <SelectItem key={method.id} value={method.id!}>
                      <div className="flex items-center space-x-2">
                        <Bitcoin className="w-4 h-4 text-orange-500" />
                        <span>{method.details.wallet || 'Bitcoin Wallet'} ({method.details.last4})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedMethodId === 'add-new' && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddWallet(true)}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Bitcoin Wallet
              </Button>
            )}

            {selectedMethodId && selectedMethodId !== 'add-new' && (
              <Button
                type="button"
                onClick={handleBitcoinPayment}
                disabled={disabled || loading}
                className="w-full"
              >
                {loading ? 'Processing...' : `Pay ${amount.toFixed(8)} BTC`}
              </Button>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No Bitcoin wallets found. Add a wallet to pay with Bitcoin.
              </AlertDescription>
            </Alert>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAddWallet(true)}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Bitcoin Wallet
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

